import React from 'react';
import styled, { DefaultTheme } from 'styled-components';

export type InputVariant = 'main' | 'secondary' | 'mini';
export type InputMessageType = 'error' | 'warning';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  variant?: InputVariant;
  label?: string;
  message?: { type: InputMessageType; content: React.ReactNode };
}

export const Input: React.FC<InputProps> = ({ className, variant = 'main', label, message, ...inputProps }) => {
  return (
    <S.Container className={className}>
      {label && variant === 'main' && <S.Label required={inputProps.required}>{label}</S.Label>}
      <S.Input messageType={message?.type} {...inputProps}></S.Input>
      {message && <S.Message type={message.type}>{message.content}</S.Message>}
    </S.Container>
  );
};

const mapMessageTypeToColor = (messageType: InputMessageType, theme: DefaultTheme): string => {
  switch (messageType) {
    case 'error':
      return theme.redNotification;
    case 'warning':
      return theme.feedbackColors.deepOrange;
  }
};

const S = {
  Container: styled.div``,
  Label: styled.div<{ required?: boolean }>`
    font-weight: 600;
    font-size: 15px;
    line-height: 19px;
    color: ${({ theme }) => theme.algoanBlue};
    margin-bottom: 8px;

    ${({ required }) =>
      required
        ? `
      &:after {
        content: '*';
      }
      `
        : ''}
  `,
  Input: styled.input<{ messageType?: InputMessageType }>`
    width: 100%;
    background: white;
    border-radius: 8px;
    box-sizing: border-box;
    border: 1px solid
      ${({ messageType, theme }) =>
        messageType ? mapMessageTypeToColor(messageType, theme) : theme.secondary.softBlue1};

    &:focus-within,
    &:hover {
      ${({ messageType, theme }) => (messageType ? '' : `border: 1px solid ${theme.secondary.softBlue3}`)}
    }

    font-size: 15px;
    line-height: 19px;
    color: ${({ theme }) => theme.algoanBlue};
    height: 45px;
    outline: none;
    padding: 13px 15px;

    &::placeholder {
      color: ${(props) => props.theme.secondary.softBlue1};
    }

    &:focus {
      outline: none;
    }
  `,
  Message: styled.div<{ type: InputMessageType }>`
    font-size: 12px;
    line-height: 15px;
    margin-top: 8px;
    color: ${({ theme, type }) => (type === 'error' ? theme.redNotification : theme.feedbackColors.deepOrange)};
  `,
};
